import { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Sun,
  Calendar,
  Tag,
  ClipboardCheck,
  ScanLine,
  Settings,
  LogOut,
  Menu,
  Store,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { NavLink } from '@/app/NavLink';
import { useAwaitingAnswerSignal } from '@/app/useAwaitingAnswerSignal';
import { useAuth } from '@/features/auth/AuthContext';
import { LEGAL_DOCUMENTS_URL } from '@/domain/user/legal';
import { useAwaitingAnswerCount } from '@/features/today/hooks/useAwaitingAnswerCount';
import { usePushSelfHeal } from '@/features/settings/hooks/usePushNotifications';
import { useUserBusiness } from '@/features/settings/hooks/useUserBusiness';
import { LGoBadge, type LGoBadgeProps } from '@/ui/branded';
import { Button } from '@/ui/primitives/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/ui/primitives/sidebar';
import { Separator } from '@/ui/primitives/separator';
import { cn } from '@/lib/cn';
import { EnvBadge } from '@/ui/patterns';

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  countsAwaitingAnswer?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = [
  {
    title: 'Denní práce',
    items: [
      { title: 'Ověření kódu', url: '/validate', icon: ScanLine },
      { title: 'Dnes', url: '/', icon: Sun },
      { title: 'Slevové termíny', url: '/offers', icon: Tag },
      { title: 'Rezervace', url: '/reservations', icon: ClipboardCheck, countsAwaitingAnswer: true },
    ],
  },
  {
    title: 'Nastavení',
    items: [
      { title: 'Šablony slev', url: '/activities', icon: Calendar },
      { title: 'Nastavení podniku', url: '/settings', icon: Settings },
    ],
  },
];

const businessIdentityTones = {
  sidebar: { name: 'text-sidebar-foreground', muted: 'text-sidebar-muted' },
  header: { name: 'text-foreground', muted: 'text-muted-foreground' },
};

interface BusinessIdentityProps {
  tone: keyof typeof businessIdentityTones;
  className?: string;
}

function BusinessIdentity({ tone, className }: BusinessIdentityProps) {
  const { business, error, isLoading } = useUserBusiness();
  const { name, muted } = businessIdentityTones[tone];

  if (isLoading) return null;

  if (error) {
    return (
      <div className={cn('flex items-center gap-2 min-w-0', muted, className)}>
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span className="text-xs leading-tight">Podnik se teď nepodařilo načíst</span>
      </div>
    );
  }

  if (!business) return null;

  return (
    <div className={cn('flex items-center gap-2 min-w-0', className)}>
      <Store className={cn('h-3.5 w-3.5 shrink-0', muted)} />
      <span className={cn('truncate text-sm font-medium', name)} title={business.name}>
        {business.name}
      </span>
    </div>
  );
}

function AwaitingAnswerCount({
  count,
  size = 'sm',
  className,
}: {
  count: number;
  size?: LGoBadgeProps['size'];
  className?: string;
}) {
  return (
    <LGoBadge role="status" variant="attention" size={size} className={cn('tabular-nums', className)}>
      <span className="sr-only">Čeká na odpověď: </span>
      {count}
    </LGoBadge>
  );
}

function AppSidebarContent() {
  const { signOut, user } = useAuth();
  const { state, isMobile } = useSidebar();
  const collapsed = state === 'collapsed' && !isMobile;
  const awaitingAnswer = useAwaitingAnswerCount();

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarRail />
      <SidebarHeader className="p-4 group-data-[collapsible=icon]:p-2">
        <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
          {collapsed ? (
            <SidebarTrigger aria-label="Rozbalit menu" className="h-8 w-8" />
          ) : (
            <>
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shrink-0 overflow-hidden">
                <img
                  src="/lastgo-mark.png"
                  alt="LastGO"
                  className="h-7 w-7 object-contain"
                  draggable={false}
                />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold tracking-tight text-sidebar-foreground leading-none">
                  LastGO<span className="text-primary">.</span>
                </h1>
                <p className="text-[10px] uppercase tracking-[0.2em] text-sidebar-muted mt-1">Partner</p>
              </div>
              <SidebarTrigger aria-label="Sbalit menu" className="ml-auto shrink-0" />
            </>
          )}
        </div>
        {!collapsed && <BusinessIdentity tone="sidebar" className="mt-3 px-1" />}
      </SidebarHeader>

      <Separator className="bg-sidebar-border" />

      <SidebarContent className="px-2 py-4 group-data-[collapsible=icon]:px-0">
        {navGroups.map((group) => (
          <SidebarGroup key={group.title}>
            {!collapsed && (
              <SidebarGroupLabel className="text-sidebar-muted">{group.title}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <NavLink
                        to={item.url}
                        end={item.url === '/'}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                        activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        <span className={cn(collapsed && "sr-only")}>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                    {item.countsAwaitingAnswer && awaitingAnswer !== null && awaitingAnswer > 0 && (
                      <AwaitingAnswerCount
                        count={awaitingAnswer}
                        size={collapsed ? 'xs' : 'sm'}
                        className={cn(
                          'pointer-events-none absolute z-10',
                          collapsed ? 'right-0 top-0' : 'right-2 top-1/2 -translate-y-1/2',
                        )}
                      />
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="p-4 group-data-[collapsible=icon]:p-2">
        <Separator className="bg-sidebar-border mb-4" />
        {!collapsed && user && (
          <p className="text-xs text-sidebar-muted mb-2 truncate px-1">
            {user.email}
          </p>
        )}
        <Button
          variant="ghost"
          asChild
          className={cn(
            "w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent",
            collapsed && "justify-center px-0"
          )}
        >
          <a
            href={LEGAL_DOCUMENTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Podmínky pro partnery"
          >
            <FileText className="h-5 w-5 shrink-0" />
            {!collapsed && <span className="ml-3">Podmínky pro partnery</span>}
          </a>
        </Button>
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className={cn(
            "w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-destructive",
            collapsed && "justify-center px-0"
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="ml-3">Odhlásit se</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}

interface AppLayoutProps {
  children: ReactNode;
}

/** The layout remounts on every navigation, so the sidebar starts from the
 *  cookie the primitive writes — otherwise a collapse the partner chose is
 *  thrown away by the next page. */
function hasPartnerCollapsedSidebar() {
  return document.cookie
    .split('; ')
    .some((entry) => entry.trim() === 'sidebar:state=false');
}

export default function AppLayout({ children }: AppLayoutProps) {
  const awaitingAnswer = useAwaitingAnswerCount();
  useAwaitingAnswerSignal(awaitingAnswer);
  usePushSelfHeal();

  return (
    <SidebarProvider defaultOpen={!hasPartnerCollapsedSidebar()}>
      <EnvBadge />
      <div className="min-h-screen flex w-full">
        <AppSidebarContent />
        
        <div className="flex-1 flex flex-col">
          <header className="h-14 border-b border-border flex items-center px-4 lg:hidden">
            <SidebarTrigger className="shrink-0">
              <Menu className="h-5 w-5" />
            </SidebarTrigger>
            <div className="ml-3 flex min-w-0 flex-1 items-center gap-2">
              <div className="w-7 h-7 rounded bg-primary flex items-center justify-center overflow-hidden shrink-0">
                <img
                  src="/lastgo-mark.png"
                  alt="LastGO"
                  className="h-5 w-5 object-contain"
                  draggable={false}
                />
              </div>
              <span className="font-display font-semibold tracking-tight text-foreground shrink-0">
                LastGO<span className="text-primary">.</span>
              </span>
              <BusinessIdentity tone="header" className="border-l border-border pl-2" />
              {awaitingAnswer !== null && awaitingAnswer > 0 && (
                <AwaitingAnswerCount count={awaitingAnswer} className="ml-auto shrink-0 md:hidden" />
              )}
            </div>
          </header>

          <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
